import { useEffect, useRef } from 'react';

import { getUserProfile } from '@/configuration/login/loginSlice';
import { useAppDispatch, useAppSelector } from '@/configuration/setup/hooks';
import { threadApi } from '@/services/threadApi/threadApi';
import {
  finishActiveThreadRestore,
  selectActiveThreadId,
  selectIsActiveThreadRestorePending,
  startActiveThreadRestore,
} from '@/store/thread/threadSlice';
import { ONBOARDING_THREAD_ID } from '@/utils/constants';
import { getUserId } from '@/utils/getUserId';
import Logger from '@/utils/logger';
import { isTemporaryThreadId } from '@/utils/temporaryUtils/temporaryIds';

const LOGGER_TAG = 'useRestoreActiveThread';

/**
 * Restores the messages of the thread persisted in sessionStorage once, on chat bootstrap.
 *
 * Fetches by thread id directly instead of waiting for the thread to appear in the paginated
 * sidebar list, so restoration works for threads outside the first page. The lifecycle is always
 * driven to its terminal state, including on failure, so the chat empty-state gate can never hang.
 */
const useRestoreActiveThread = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUserProfile);
  const userId = getUserId(user?.sub ?? '');
  const activeThreadId = useAppSelector(selectActiveThreadId);
  const isRestorePending = useAppSelector(selectIsActiveThreadRestorePending);
  const [fetchMessagesByThreadId] = threadApi.endpoints.fetchMessagesByThreadId.useLazyQuery();
  const hasStartedRef = useRef(false);

  useEffect(() => {
    // INFO: Guards against a second run when React re-invokes the effect (StrictMode, remount)
    if (hasStartedRef.current) return;
    // INFO: Restoration already settled — nothing persisted, or a thread was explicitly selected
    if (!isRestorePending) return;

    const threadId = activeThreadId;
    const isRestorable = Boolean(threadId) && threadId !== ONBOARDING_THREAD_ID && !isTemporaryThreadId(threadId ?? '');

    if (!threadId || !isRestorable) {
      hasStartedRef.current = true;
      dispatch(finishActiveThreadRestore());
      return;
    }

    // INFO: The profile arrives asynchronously; without it the request would run on an empty user id
    if (!userId) return;

    hasStartedRef.current = true;
    dispatch(startActiveThreadRestore());

    const restore = async () => {
      try {
        await fetchMessagesByThreadId({ userId, threadId }).unwrap();
      } catch (error) {
        Logger.error(LOGGER_TAG, 'Failed to restore the active thread', error);
      } finally {
        dispatch(finishActiveThreadRestore());
      }
    };

    restore();
    // INFO: userId — the fetch cannot run before the profile resolves
    // INFO: activeThreadId — identifies which thread to restore
    // INFO: isRestorePending — stops the effect once the lifecycle is terminal
    // INFO: dispatch / fetchMessagesByThreadId — stable references required by the linter
  }, [userId, activeThreadId, isRestorePending, dispatch, fetchMessagesByThreadId]);
};

export default useRestoreActiveThread;
